import { ArrayUtils } from "../../../utilities/arrays";
import { ExcelToEntityMap } from "../../../models/data-migration/excel-to-entity-map";
import { PropertyMap } from "../../../models/data-migration/property-map";
import { autoinject } from "aurelia-dependency-injection";
import { bindable } from "aurelia-templating";
import { type ImportEntityMetaDataModel } from "../../../models/import-entities/import-entities.models";
import * as Enumerable from "linq";
import { CultureInfoService } from "../../../services/internationalization/culture-info.service";
import { type CultureInfo } from "../../../models/internationalization/culture-info.models";
import { DataMigrationError, DataMigrationErrorTypes } from "../../../models/data-migration/data-migration-errors.models";
import { I18N } from "aurelia-i18n";
import { ExcelImportMapping, ExcelImportMappingRequest } from "../../../models/data-migration/excel-import-mapping";
import { RequestClientService } from "@rightship-tech/common-ui/services/basic/request-client.service";
import { UnitOfWorkObserver, UnitOfWorkAction } from "../../../utilities/unit-of-work-observer";
import { BindingEngine, computedFrom } from "aurelia-binding";
import { ComboBox } from "../combo-box/combo-box";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { ExcelImportSharedDialogModel } from "./dialogs/excel-import-shared-dialog";
import { StringUtilities } from "../../../utilities/strings";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { Reflection } from "../../../utilities/reflection";
import { FileHelper } from "../../../helpers/file-helper";
import * as DOMPurify from "dompurify";
import { NotificationClientService, NotificationType } from "../../../services/basics/notification-client.service";
import { LookupValueRequest } from "../../../models/lookups/lookup.models";
import { LookupClientService } from "../../../services/lookups/lookup-client.service";
import { AllEntityTypeConfigurationConstants, DynamicColumnModel, EntityTypeConfigurationModel } from "../../../models/basics/basics.models";
import { DataType } from "../../../models/queries/queries.models";
import { PLATFORM } from "aurelia-framework";

const BaseUri = "ExcelImportMappings";
type ExcelStep = 'upload' | 'mappings' | 'finish' | 'error';

export const DefaultExcelSteps: ExcelStep[] = ['upload', 'mappings', 'finish', 'error'];

@autoinject
export class ExcelToDbImportPlugin {

    private readonly _supportedTypes = [
        //Bug Fix for chrome that does not manage to return the type of the file sometimes
        {
            type: "xlsx",
            mime: ""
        },
        {
            type: "xlsx",
            mime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        },
        {
            type: "xlsm",
            mime: "application/vnd.ms-excel.sheet.macroEnabled.12"
        },
        {
            type: "xlsb",
            mime: "application/vnd.ms-excel.sheet.binary.macroEnabled.12"
        },
        {
            type: "ods",
            mime: "application/vnd.oasis.opendocument.spreadsheet"
        },
        {
            type: "csv",
            mime: "text/csv"
        },
        {
            type: "csv",
            mime: "application/vnd.ms-excel",
            extension: "csv" //added because this mime type is also for xls
        }
    ];

    private _file: File;
    private _dataSetUows: UnitOfWorkObserver<ExcelImportMapping>[] = [];

    public fileInput: HTMLInputElement;
    public isNewConfiguration: boolean = false;
    public configurations: ExcelImportMapping[] = []; // Existing Configurations
    public dataSets: ExcelImportDataSet[] = [];
    public lookups: { displayName: string, lookupName: string }[] = [];
    public entityTypeConfigurations: EntityTypeConfigurationModel[];
    private mappings: ExcelToEntityMap[] = [];

    @bindable
    selectedFilesFromDialog: FileList;

    @bindable
    public title: string = "";

    @bindable
    public propertyMatchResolver: (excelHeaderNormalized: string, propertyMap: PropertyMap, selectedEntityModel: ImportEntityMetaDataModel) => boolean = null;

    @bindable
    public entity: ImportEntityMetaDataModel;

    @bindable
    public textImportButton: string = "Import From Excel";

    @bindable
    public icon: string = "";

    @bindable
    public entityPropertiesHeader: string = "Map to List Field";

    @bindable
    public postUrl: string = "";

    @bindable
    public enableCulture: boolean = false;

    @bindable
    public culture: CultureInfo;

    @bindable
    public showBack: boolean = true;

    @bindable
    public templateUri: string;

    @bindable
    public errors: DataMigrationError[];

    @bindable
    public filter: string;

    @bindable
    public steps: ExcelStep[] = DefaultExcelSteps;

    @bindable
    public step: ExcelStep = 'upload';

    @bindable
    public mappingsConfiguration: boolean = false;

    @bindable
    public setUnmatchedAsDynamic: boolean = false;

    @bindable
    public showLookupEntityFinder: boolean = true;

    @bindable
    public showDynamicLookupCategory: boolean = true;
   
    @computedFrom(Reflection.getPropertyPath<ExcelToDbImportPlugin>(z => z.cultureInfoService.cultures),
        Reflection.getPropertyPath<ExcelToDbImportPlugin>(z => z.cultureInfoService.cultures.length))
    public get cultures(): CultureInfo[] {
        return this.cultureInfoService.cultures;
    }

    constructor(private element: Element,
        private cultureInfoService: CultureInfoService,
        private requestClient: RequestClientService,
        private lookupClientService: LookupClientService,
        private i18N: I18N,
        private bindingEngine: BindingEngine,
        private notificationClientService: NotificationClientService,
        private dialogService: DialogClientService) {
        this.lookupClientService.getLookup(new LookupValueRequest('lookups')).then(response => {
            this.lookups = response.allValuesIncludingInactive.map(val => ({ displayName: val.name, lookupName: val.additionalData.lookupName }));
        });
        this.entityTypeConfigurations = AllEntityTypeConfigurationConstants.filter(x => x.uri && x.properties && (!x.excludeInDashboard || !x.excludeInDashboard()));

    }

    async attached() {
        this._file = null;
        if (this.fileInput) {
            this.fileInput.value = "";
        }
        this.onNewExcelUploaded(null);
        this.culture = this.cultureInfoService.findByCode('en-GB');
        const mappings = await this.getSavedMappings();
        this.configurations = this.configurations.concat(mappings);
    }

    detached() {
        this.resetExcelImport(undefined, true);
    }

    entityChanged(): void {
        this.onNewExcelUploaded(null);
    }

    onIncludeForImportChanged(dataSetIndex: number, mapping: ExcelToEntityMap): void {
        if (!mapping.includeForImport) {
            return;
        }
        mapping.column = null;
    }

    errorsChanged(errors: DataMigrationError[]) {
        if (errors && errors.length > 0) {
            this.step = 'error';
        }
    }

    getErrorMessage(error: DataMigrationError): string {
        if (error.errorMessage && error.errorMessage.trim()) {
            let translated = this.i18N.tr(error.errorMessage);

            if (error.errorMessageParams && error.errorMessageParams.length > 0)
                translated = StringUtilities.formatString(translated, error.errorMessageParams);

            return DOMPurify.sanitize(translated);
        }

        switch (error.errorType) {
            case DataMigrationErrorTypes.Error:
                return this.i18N.tr('excel-error-unkown-text');
            case DataMigrationErrorTypes.InvalidFormat:
                return this.i18N.tr('excel-error-format-text');
            case DataMigrationErrorTypes.InvalidFormatDate:
                return this.i18N.tr('excel-error-format-date-text');
            case DataMigrationErrorTypes.InvalidFormatEmail:
                return this.i18N.tr('excel-error-format-email-text');
            case DataMigrationErrorTypes.InvalidFormatNumber:
                return this.i18N.tr('excel-error-format-number-text');
            case DataMigrationErrorTypes.InvalidFormatPhone:
                return this.i18N.tr('excel-error-format-phone-text');
            case DataMigrationErrorTypes.MappedEntityNotFound:
                return this.i18N.tr('excel-error-mapped-entity-not-found-text');
            case DataMigrationErrorTypes.Required:
                return this.i18N.tr('excel-error-required-text');
            case DataMigrationErrorTypes.DuplicateMappings:
                {
                    const duplicateProperties = error.errorMessageParams.map(c => {
                        const property = this.entity.properties.filter(v => v.property == c)[0];
                        if (property) {
                            return property.displayName;
                        }

                        const dynamicProperty = this.entity.dynamicProperties.filter(dp => dp.columnName.toLowerCase() == c.toLowerCase())[0];


                        if (dynamicProperty) {
                            return dynamicProperty.displayName;
                        }

                        const newMappingProperty = this.mappings.filter(x => x.column.toLowerCase() == c.toLowerCase())[0];
                        return newMappingProperty.column;
                    });

                    return StringUtilities.formatString(this.i18N.tr('excel-error-duplicate-mappings-text'), [duplicateProperties.join(", ")]);
                }
        }
    }

    onDynamicColumnSelected(mapping: ExcelToEntityMap, item: DynamicColumnModel) {
        if (item) {
            mapping.typeId = item.tableColumnDataTypeId;
            mapping.typeParameter = item.parameter;
            mapping.secondTypeParameter = item.secondParameter;
        }
    }

    onNewExcelUploaded($event: any): void {
        if (!$event) {
            this._file = null;
            return;
        }

        $event.preventDefault();
        const source = ($event.srcElement || $event.target) as HTMLInputElement;
        this._file = source.files.item(0);
        $event.srcElement.value = "";

        const fileExtension = this._file && this._file.name ? FileHelper.getFileExtension(this._file.name) : null;

        if (!this._file || !this._supportedTypes.some(x => x.mime === this._file.type && (!x.extension || !fileExtension || (x.extension == fileExtension)))) {
            this.remove(this.dataSets[0], false);
            const error: DataMigrationError = {
                errorMessage: "File type not supported.",
                errorType: DataMigrationErrorTypes.Error,
                errorMessageParams: null,
                rangeEnd: null,
                rangeStart: null,
                validatedBlock: null,
                worksheet: null
            }
            this.errors = [error];
            return;
        }

        const dataSet = new ExcelImportDataSet();
        dataSet.excelFile = this._file;
        dataSet.defaultMappings = [];

        const element = this.element;
        const reader = new FileReader();
        const excelToEntityMappings = dataSet.defaultMappings;
        const dataSets = this.dataSets;
        reader.onload = (e: any) => {

            import('xlsx').then((XLSX) => {
                const data = e.target.result as string;
                const workbook = XLSX.read(data, { type: "binary" });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const range = XLSX.utils.decode_range(sheet["!ref"]);

                const ignoredExcelColumns = this.entity && this.entity.excelIgnoredColumns ? this.entity.excelIgnoredColumns : [];

                for (let i = range.s.c; i <= range.e.c; i++) {
                    const header = sheet[XLSX.utils.encode_cell({ c: i, r: 0 })];
                    if (!header) continue;

                    const excelColumn = header.h ? header.h : <string>header.v;

                    //Only headers that are string (in some CSV files headers with integer values were found.)
                    if (header.t == "s" && ignoredExcelColumns.filter(x => x === excelColumn).length == 0) {
                        const mapping: ExcelToEntityMap = {
                            dynamicPropertyFilter: this.filter,
                            excelColumn: excelColumn,
                            id: excelColumn,
                            sequenceNumber: i,
                            column: null,
                            includeForImport: true,
                            isDynamicProperty: false,
                            isNewDynamicProperty: false,
                            typeId: null,
                            typeParameter: null,
                            secondTypeParameter: null
                        }

                        const matchingProperty = this.getMatchingProperty(excelColumn);

                        if (matchingProperty.length > 0 && mapping) {
                            mapping.column = matchingProperty[0].property;
                        }
                        else if (this.setUnmatchedAsDynamic) {
                            mapping.isDynamicProperty = true;
                        }

                        excelToEntityMappings.push(mapping);
                    }
                }

                const mapping = this.generateDefaultConfiguration(dataSet);
                dataSets.push(dataSet);
                dataSet.mapping = mapping;

                const defaultIndex = this.configurations.indexOf(this.getDefaultConfiguration());

                if (defaultIndex >= 0) {
                    this.configurations.splice(defaultIndex, 1);
                }

                this.configurations.splice(0, 0, mapping);

                element.dispatchEvent(new CustomEvent("on-excel-loaded", {
                    bubbles: true,
                }));

                this.nextStep();
            });
        };

        reader.readAsBinaryString(this._file);
    }

    getEditDistance = function (a, b) {
        if (a.length == 0) return b.length;
        if (b.length == 0) return a.length;

        const matrix = [];

        // increment along the first column of each row
        let i;
        for (i = 0; i <= b.length; i++) {
            matrix[i] = [i];
        }

        // increment each column in the first row
        let j;
        for (j = 0; j <= a.length; j++) {
            matrix[0][j] = j;
        }

        // Fill in the rest of the matrix
        for (i = 1; i <= b.length; i++) {
            for (j = 1; j <= a.length; j++) {
                if (b.charAt(i - 1) == a.charAt(j - 1)) {
                    matrix[i][j] = matrix[i - 1][j - 1];
                } else {
                    matrix[i][j] = Math.min(matrix[i - 1][j - 1] + 1, // substitution
                        Math.min(matrix[i][j - 1] + 1, // insertion
                            matrix[i - 1][j] + 1)); // deletion
                }
            }
        }

        return matrix[b.length][a.length];
    };

    remove(dataSet: ExcelImportDataSet, raiseEvent: boolean = true): void {
        const index = ArrayUtils.findIndex(this.dataSets, x => dataSet === x);
        this.dataSets.splice(index, 1);
        if (raiseEvent) {
            this.element.dispatchEvent(new CustomEvent("on-excel-unloaded",
                {
                    bubbles: true,
                }));
            this.nextStep(false);
        }
    }

    nextStep(isForward = true) {
        const index = this.steps.indexOf(this.step);
        this.step = isForward
            ? index < this.steps.length
                ? this.steps[index + 1]
                : this.step
            : index > 0
                ? this.steps[index - 1]
                : this.step;
    }

    back() {
        this.element.dispatchEvent(new CustomEvent("on-back", {
            bubbles: true,
        }));
        this.nextStep(false);
    }

    import(excelDataSet: ExcelImportDataSet) {
        this.mappings = excelDataSet.mapping.mappings ? excelDataSet.mapping.mappings : [];
        let duplicates = Enumerable.from(this.mappings).where(x => x.column != null && x.includeForImport).groupBy(x => x.column).where(x => x.count() > 1);
        duplicates = duplicates.where(c => !!c.key());
        if (duplicates.any()) {
            const keys = duplicates.select(x => x.key()).toArray();
            const duplicateProperties = keys.map(c => {
                const property = this.entity.properties.filter(v => v.property == c)[0];
                if (property) {
                    return property.displayName;
                }

                const dynamicProperty = this.entity.dynamicProperties.filter(dp => dp.columnName.toLowerCase() == c.toLowerCase())[0];

                if (dynamicProperty) {
                    return dynamicProperty.displayName;
                }

                const newMappingProperty = this.mappings.filter(x => x.column.toLowerCase() == c.toLowerCase())[0];
                return newMappingProperty.column;
            });
            const message = StringUtilities.formatString(this.i18N.tr('excel-error-duplicate-mappings-text'), [duplicateProperties.join(", ")]);
            this.notificationClientService.show("Error", message, NotificationType.Error);
            return;
        }

        const mappingsWithMissingDataTypeParameter = Enumerable.from(this.mappings).where(c =>
            c.column != null &&
            c.includeForImport &&
            c.isDynamicProperty &&
            [DataType.Lookup, DataType.HierarchicalLookup, DataType.EntityFinder].indexOf(c.typeId) > -1 &&
            !c.typeParameter)
            .toArray();
        if (mappingsWithMissingDataTypeParameter.length > 0) {
            const props = mappingsWithMissingDataTypeParameter.map(x => x.excelColumn);
            const message = StringUtilities.formatString(this.i18N.tr('excel-error-data-type-parameter-mappings-text'), [props.join(", ")]);
            this.notificationClientService.show("Error", message, NotificationType.Error);
            return;
        }

        const incompleteMappings = Enumerable.from(this.mappings).where(c => !c.column && c.includeForImport)
            .toArray();
        if (incompleteMappings.length > 0) {
            const props = incompleteMappings.map(x => x.excelColumn);
            const message = StringUtilities.formatString(this.i18N.tr('excel-error-column-not-set-mappings-text'), [props.join(", ")]);
            this.notificationClientService.show("Error", message, NotificationType.Error);
            return;
        }

        const duplicateDynamicColumnsMappings = Enumerable.from(this.mappings).where(c =>
            c.column &&
            c.includeForImport &&
            c.isDynamicProperty &&
            c.isNewDynamicProperty &&
            this.entity.dynamicProperties.some(dp => dp.displayName.toLowerCase() == c.column.toLowerCase()))
            .toArray();
        if (duplicateDynamicColumnsMappings.length > 0) {
            const props = duplicateDynamicColumnsMappings.map(x => x.excelColumn);
            const message = StringUtilities.formatString(this.i18N.tr('excel-error-duplicate-dynamic-column-mappings-text'), [props.join(", ")]);
            this.notificationClientService.show("Error", message, NotificationType.Error);
            return;
        }

        const mappingsWithMissingSecondParameter = Enumerable.from(this.mappings).where(c =>
            c.column != null &&
            c.includeForImport &&
            c.isDynamicProperty &&
            c.isNewDynamicProperty &&
            [DataType.Lookup].indexOf(c.typeId) > -1 &&
            c.typeParameter == "DynamicLookupValues" &&
            !c.secondTypeParameter)
            .toArray();
        if (mappingsWithMissingSecondParameter.length > 0) {
            const props = mappingsWithMissingSecondParameter.map(x => x.excelColumn);
            const message = StringUtilities.formatString(this.i18N.tr('excel-error-second-data-type-parameter-mappings-text'), [props.join(", ")]);
            this.notificationClientService.show("Error", message, NotificationType.Error);
            return;
        }

        const paramsEvent: any = { detail: excelDataSet, bubbles: true };
        const changeTextEvent: CustomEvent = new CustomEvent("on-import", paramsEvent);
        this.element.dispatchEvent(changeTextEvent);
        this.nextStep();
    }

    resetExcelImport(step: ExcelStep = undefined, isResetConfiguration: boolean = false) {
        this.dataSets = [];
        this._dataSetUows = [];
        this.errors = [];
        this.step = step
            ? step
            : this.steps[0];

        const stepIndex = this.steps.indexOf(this.step);
        const defaultStepIndex = this.steps.indexOf(DefaultExcelSteps[0]);

        if (isResetConfiguration || defaultStepIndex >= 0 && stepIndex >= 0 && stepIndex < defaultStepIndex) {
            this.configurations = [];
        }
    }

    sortConfigurations() {
        this.configurations = this.configurations.sort((left, right) => {
            const grouping = +!!left.userId - +!!right.userId;

            if (grouping != 0)
                return grouping;

            return +(left.displayName.toLowerCase() > right.displayName.toLowerCase());
        });
    }

    toggleConfiguration(dataSetIndex: number, savedMappings: ComboBox, isNew: boolean = true) {
        savedMappings.closePopup();
        this.isNewConfiguration = !this.isNewConfiguration;

        if (isNew) {
            const mappings = CloneHelper.cloneObject(this.dataSets[dataSetIndex].mapping.mappings);
            this.dataSets[dataSetIndex].mapping = {
                excelImportMappingId: 0,
                displayName: '',
                entityTypeId: this.entity.entityType,
                mappings: null,
                isDefault: false,
            };
            this._dataSetUows[dataSetIndex] = UnitOfWorkObserver.getTrackingInstance(this.bindingEngine, this.dataSets[dataSetIndex].mapping);
            this.dataSets[dataSetIndex].mapping.mappings = mappings;
            this.configurations.push(this.dataSets[dataSetIndex].mapping);
        }
    }

    saveConfiguration(dataSetIndex: number) {
        return new Promise<void>((resolve, reject) => {
            const index = this.configurations.indexOf(this.dataSets[dataSetIndex].mapping);

            const save = async (isShared?: boolean) => {
                const mapping = await this.saveMappings(dataSetIndex, isShared);
                mapping.mappings = JSON.parse(<any>mapping.mappings);

                if (index >= 0) {
                    this.configurations.splice(index, 1);
                }

                this.removeUnsavedConfiguration();
                this.configurations.push(mapping);
                this.sortConfigurations();

                this.dataSets[dataSetIndex].mapping = mapping;

                resolve();
                this.isNewConfiguration = false;
                this.dialogService.hide();
            }

            const cancel = () => {
                reject();
                this.isNewConfiguration = false;
                this.dialogService.hide();
            }

            if (index == -1) {
                this.dialogService.show(PLATFORM.moduleName('views/components/excel-to-db-import-plugin/dialogs/excel-import-shared-dialog'), <ExcelImportSharedDialogModel>{
                    onSave: save,
                    onCancel: cancel
                });
                return;
            }

            save();
            this.removeUnsavedConfiguration();
            this.isNewConfiguration = false;
        });
    }

    async deleteConfiguration(dataSetIndex: number) {
        this.isNewConfiguration = false;
        const response = await this.deleteMappings(dataSetIndex);

        if (response) {
            const index = this.configurations.indexOf(this.dataSets[dataSetIndex].mapping);
            this.dataSets[dataSetIndex].mapping = this.getDefaultConfiguration();
            if (index < 0) return;
            this.configurations.splice(index, 1);
        }
    }

    cancelConfiguration(dataSetIndex: number) {
        this.isNewConfiguration = false;
        this.removeUnsavedConfiguration();

        if (this._dataSetUows[dataSetIndex].hasChanges()) {
            this.revertUnitOfWorkChanges(this._dataSetUows[dataSetIndex], ['displayName']);

            if (!this._dataSetUows[dataSetIndex].hasChanges()) {
                this._dataSetUows[dataSetIndex].dispose();
            }
        }

        if (this.dataSets[dataSetIndex].mapping.excelImportMappingId < 1) {
            this.dataSets[dataSetIndex].mapping = this.getDefaultConfiguration();
        }
    }

    removeUnsavedConfiguration() {
        if (this.configurations[this.configurations.length - 1].excelImportMappingId == 0) {
            this.configurations.pop();
        }
    }

    generateDefaultConfiguration(dataSet: ExcelImportDataSet) {
        return {
            excelImportMappingId: -1,
            displayName: '',
            entityTypeId: this.entity.entityType,
            mappings: CloneHelper.cloneObject(dataSet.defaultMappings),
            isDefault: true,
        };
    }

    getDefaultConfiguration() {
        const configuration = this.configurations.filter(x => x.isDefault)[0];
        return configuration;
    }

    async updateConfiguration(dataSetIndex: number) {
        await this.saveMappings(dataSetIndex);
    }

    revertUnitOfWorkChanges(unitOfWork: UnitOfWorkObserver, propertyNames: string[] = null) {
        if (!unitOfWork.hasChanges()) return;

        const changes = unitOfWork.getChanges();
        const mapping = unitOfWork['item'];

        const revert = (obj: { [index: string]: any }, item: object) => {
            for (const key of Object.keys(obj)) {
                if (propertyNames && propertyNames.indexOf(key) == -1 || key == 'isArray') continue;

                const change = obj[key];

                if (change.isArray) {
                    // let next = (item as any[]).filter(i => i.id == key)[0];
                    revert(change, item[key]);
                    continue;
                }

                if (!change.action) {
                    if (Array.isArray(item)) {
                        for (const i of item) {
                            if (i.id == key) {
                                revert(change, i);
                                break;
                            }
                        }
                        continue;
                    }
                    revert(change, item[key]);
                    continue;
                }


                switch (change.action) {
                    case UnitOfWorkAction.Added:
                        delete item[key];
                        break;
                    case UnitOfWorkAction.Modified:
                    case UnitOfWorkAction.Removed:
                    case UnitOfWorkAction.Unknown:
                        item[key] = change.oldValue;
                        break;
                }
            }
        }

        revert(changes, mapping);
    }

    getSavedMappings(): Promise<ExcelImportMapping[]> {
        return new Promise((response, error) => {
            this.requestClient.get<ExcelImportMapping[]>(`${BaseUri}/${this.entity.entityType}`).then((success: ExcelImportMapping[]) => {
                success.forEach(m => {
                    m.mappings = JSON.parse(<any>m.mappings);
                });
                response(success);
            }, e => {
                error(e);
            });
        });
    }

    saveMappings(dataSetIndex: number, isGlobal: boolean = null): Promise<ExcelImportMapping> {
        return new Promise((response, error) => {
            const request: ExcelImportMappingRequest = {
                id: this.dataSets[dataSetIndex].mapping.excelImportMappingId,
                isGlobal: isGlobal,
                changes: this._dataSetUows[dataSetIndex].getChanges(),
            };

            const mappings = Reflection.getPropertyName<ExcelImportMapping>(m => m.mappings);

            if (request.changes[mappings]) {
                delete request.changes[mappings];
                request.changes[mappings] = {
                    action: UnitOfWorkAction.Modified,
                    oldValue: null,
                    value: JSON.stringify(this.dataSets[dataSetIndex].mapping.mappings)
                }
            }

            this.requestClient.post<ExcelImportMapping>(`${BaseUri}/${this.entity.entityType}`, null, request).then((mapping: ExcelImportMapping) => {
                if (this._dataSetUows[dataSetIndex]) {
                    this._dataSetUows[dataSetIndex].clearChanges();
                }
                response(mapping);
            }, e => {
                error(e);
            });
        });
    }

    deleteMappings(dataSetIndex: number): Promise<boolean> {
        return new Promise((response, error) => {
            this.requestClient.post<boolean>(`${BaseUri}/delete`, null, {
                id: this.dataSets[dataSetIndex].mapping.excelImportMappingId
            }).then((success: boolean) => {
                response(success);
            }, e => {
                error(e);
            });
        });
    }

    onMappingConfigurationChanged(dataSetIndex: number) {
        if (this._dataSetUows[dataSetIndex]) {
            this.revertUnitOfWorkChanges(this._dataSetUows[dataSetIndex])
            this._dataSetUows[dataSetIndex].dispose();
            delete this._dataSetUows[dataSetIndex];
        }

        if (this.dataSets[dataSetIndex].mapping.isDefault) {
            this.dataSets[dataSetIndex].mapping.mappings = CloneHelper.cloneObject(this.dataSets[dataSetIndex].defaultMappings);
            return;
        }

        const defaultIndex = this.configurations.indexOf(this.getDefaultConfiguration());

        if (defaultIndex >= 0) {
            this.configurations.splice(defaultIndex, 1);
        }

        this.configurations.splice(0, 0, this.generateDefaultConfiguration(this.dataSets[dataSetIndex]));

        this._dataSetUows[dataSetIndex] = UnitOfWorkObserver.getTrackingInstance(this.bindingEngine, this.dataSets[dataSetIndex].mapping);

        setTimeout(() => {
            ArrayUtils.removeIf(this.dataSets[dataSetIndex].mapping.mappings, c => this.dataSets[dataSetIndex].defaultMappings.every(v => v.excelColumn != c.excelColumn));

            this.dataSets[dataSetIndex].defaultMappings.forEach(c => {
                const index = this.dataSets[dataSetIndex].mapping.mappings.findIndex(v => v.excelColumn == c.excelColumn);

                if (index == -1) {
                    const newMapping: ExcelToEntityMap = {
                        id: c.id,
                        column: c.column,
                        excelColumn: c.excelColumn,
                        includeForImport: c.includeForImport,
                        sequenceNumber: c.sequenceNumber,
                        typeId: c.typeId,
                        dynamicPropertyFilter: this.filter,
                        isDynamicProperty: c.isDynamicProperty,
                        isNewDynamicProperty: c.isNewDynamicProperty,
                        typeParameter: c.typeParameter,
                        secondTypeParameter: c.secondTypeParameter
                    }
                    this.dataSets[dataSetIndex].mapping.mappings.push(newMapping);
                }
            });
        });
    }

    onIsDynamicPropertyChanged(mapping: ExcelToEntityMap) {
        if (mapping.isDynamicProperty) {
            this.onIsNewDynamicPropertyChanged(mapping);
        } else {
            const matchingProperty = this.getMatchingProperty(mapping.excelColumn);
            if (matchingProperty.length > 0 && mapping) {
                mapping.column = matchingProperty[0].property;
            }
        }

    }

    onIsNewDynamicPropertyChanged(mapping: ExcelToEntityMap) {
        if (mapping.isNewDynamicProperty) {
            mapping.column = mapping.excelColumn;
        } else {
            const dynamicColumn = this.entity.dynamicProperties.filter(x => x.displayName.toLowerCase() == mapping.excelColumn.toLowerCase())[0];
            mapping.column = dynamicColumn ? dynamicColumn.columnName : null;
        }
    }

    private getMatchingProperty(excelColumn: string) {

        const entityProperties = this.entity.properties;

        const normalize = (input: string): string => {
            return input.replace(/\s/g, '').toLowerCase();
        }

        const headerNormalized = normalize(excelColumn);
        let matchingProperty = entityProperties.filter(x => {
            let anyMatches: boolean = this.propertyMatchResolver
                ? this.propertyMatchResolver(headerNormalized, x, this.entity)
                : false;

            anyMatches = !anyMatches && x.possibleNameMatches
                ? Enumerable.from(x.possibleNameMatches).any(y => this.getEditDistance(normalize(y), headerNormalized) < 1)
                : anyMatches;

            return anyMatches;
        });

        if (matchingProperty.length == 0) {
            matchingProperty = entityProperties.filter(x => this.getEditDistance(normalize(x.property), headerNormalized) <= 2);
        }
        return matchingProperty;
    }
}

export class ExcelImportDataSet {
    public dataSetDescription: string = "";
    public tags: string[] = [];
    public excelFile: File;
    public defaultMappings: ExcelToEntityMap[] = [];
    public mapping: ExcelImportMapping;
}

export interface ExcelExplicitValue {
    property: string,
    value: any
}
